import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAllowedMenus } from "@/hooks/useAllowedMenus";
import { useImpersonationTarget } from "@/contexts/ImpersonationTargetContext";
import { useVistasDelRol } from "@/hooks/usePortalBancos/useVistasDelRol";
import {
  BarChart3,
  Inbox,
  GitBranch,
  Ticket as TicketIcon,
  type LucideIcon,
} from "lucide-react";

/** Prefijo de las rutas del portal: identifica sus submenús sin depender del nombre del menú. */
export const PORTAL_TICKETS_PREFIX = "/admin/portal-tickets/";

export interface TicketsNavItem {
  id: number;
  label: string;
  path: string;
  icon: LucideIcon;
  orden: number;
}

const ROUTE_ICON: Record<string, LucideIcon> = {
  "/admin/portal-tickets/todos": Inbox,
  "/admin/portal-tickets/analitica": BarChart3,
  "/admin/portal-tickets/pipelines": GitBranch,
};

/**
 * Nav del Portal Tickets leído 100% desde BD (menus/submenus) y filtrado por los
 * permisos del rol (`useAllowedMenus`). No hay items hardcodeados: si un submenú
 * no existe, está apagado o el rol no tiene permiso de lectura, no se muestra.
 *
 * Los submenús se ubican por ruta (`vista_front_end`), nunca por `menus.nombre`: el nombre
 * se edita desde "Administrar Menús" y renombrarlo dejaba el portal sin vistas. El orden
 * del sidebar es `submenus.orden`.
 */
export function usePortalTicketsNav() {
  const { isPathAllowed, isLoading } = useAllowedMenus();
  // "Ver como": el menú es el del rol de la persona vista, no el de la sesión. Solo cambia lo
  // que se PINTA; la autorización de rutas sigue con el rol real (no-auth-coupling.test.ts).
  const target = useImpersonationTarget();
  const { data: vistasVerComo, isPending: vistasPendientes } = useVistasDelRol(target?.rolId ?? null);

  const { data, isPending: navPendiente } = useQuery<TicketsNavItem[]>({
    queryKey: ["portal-tickets-nav"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("submenus")
        .select("id, nombre, vista_front_end, orden, menus!inner(activo)")
        .like("vista_front_end", `${PORTAL_TICKETS_PREFIX}%`)
        .eq("menus.activo", true)
        .eq("activo", true)
        .order("orden");
      if (error || !data) return [];
      return (data as any[]).map((s) => ({
        id: s.id as number,
        label: s.nombre as string,
        path: s.vista_front_end as string,
        orden: (s.orden as number) ?? 0,
        icon: ROUTE_ICON[s.vista_front_end as string] ?? TicketIcon,
      }));
    },
    staleTime: 5 * 60_000,
  });

  const items = useMemo(
    () =>
      (data ?? []).filter((i) =>
        target?.rolId != null ? (vistasVerComo?.has(i.path) ?? false) : isPathAllowed(i.path),
      ),
    [data, isPathAllowed, target?.rolId, vistasVerComo],
  );

  // Cargando = permisos, menú o (con "Ver como") las vistas del rol visto: sin esto el menú se
  // pinta vacío un instante y se confunde con "sin accesos".
  return { items, isLoading: isLoading || navPendiente || (target?.rolId != null && vistasPendientes) };
}